import type { Locale } from "@chams-pay/contracts";
import { cn } from "@chams-pay/design-system/lib/utils";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarPopover } from "./calendar-popover.js";

function yearOf(value: string): number {
  const year = Number.parseInt(value.slice(0, 4), 10);
  return Number.isInteger(year) ? year : new Date().getFullYear();
}

export function MonthPicker({
  locale,
  value,
  label,
  className,
  onChange,
}: {
  readonly locale: Locale;
  readonly value: string;
  readonly label: string;
  readonly className?: string;
  readonly onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"months" | "years">("months");
  const [year, setYear] = useState(() => yearOf(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const language = locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn";
  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, month) =>
        new Intl.DateTimeFormat(language, { month: "short" }).format(
          new Date(Date.UTC(2024, month, 1)),
        ),
      ),
    [language],
  );
  const selectedMonth = Number.parseInt(value.slice(5, 7), 10) - 1;
  const selectedYear = yearOf(value);
  const displayLabel = new Intl.DateTimeFormat(language, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(selectedYear, Math.max(0, selectedMonth), 1)));
  const yearPageStart = Math.floor(year / 12) * 12;
  const years = Array.from({ length: 12 }, (_, index) => yearPageStart + index);

  useEffect(() => setYear(selectedYear), [selectedYear]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !rootRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      )
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setView("months");
          setOpen((current) => !current);
        }}
        className="chams-focusable flex h-11 w-full items-center gap-2 rounded-md border border-input bg-card px-3 font-heading text-sm font-semibold text-foreground"
      >
        <CalendarDays aria-hidden="true" className="size-4 text-muted-2" />
        <span className="chams-figure flex-1 text-start">{displayLabel}</span>
      </button>
      <CalendarPopover
        open={open}
        anchorRef={buttonRef}
        panelRef={panelRef}
        align={locale === "ar" ? "end" : "start"}
        className="w-[19rem] rounded-lg border border-border bg-popover p-3 shadow-e3 animate-fade-up"
      >
        <div role="dialog" aria-label={label}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <button
              type="button"
              className="chams-focusable grid size-10 place-items-center rounded-md text-muted-foreground hover:bg-sunken"
              aria-label={String(year - (view === "years" ? 12 : 1))}
              onClick={() => setYear((current) => current - (view === "years" ? 12 : 1))}
            >
              <ChevronLeft aria-hidden="true" className="size-4 rtl:-scale-x-100" />
            </button>
            <button
              type="button"
              aria-pressed={view === "years"}
              className="chams-figure chams-focusable inline-flex min-h-9 items-center gap-1 rounded-md px-3 font-heading text-sm font-bold text-foreground hover:bg-sunken aria-pressed:bg-primary-soft aria-pressed:text-primary-strong"
              onClick={() => setView((current) => (current === "years" ? "months" : "years"))}
            >
              {new Intl.NumberFormat(language, { useGrouping: false }).format(year)}
              <ChevronDown aria-hidden="true" className="size-3.5" />
            </button>
            <button
              type="button"
              className="chams-focusable grid size-10 place-items-center rounded-md text-muted-foreground hover:bg-sunken"
              aria-label={String(year + (view === "years" ? 12 : 1))}
              onClick={() => setYear((current) => current + (view === "years" ? 12 : 1))}
            >
              <ChevronRight aria-hidden="true" className="size-4 rtl:-scale-x-100" />
            </button>
          </div>
          {view === "months" && (
            <div className="grid grid-cols-3 gap-1.5">
              {monthNames.map((monthName, month) => {
                const active = year === selectedYear && month === selectedMonth;
                return (
                  <button
                    key={month}
                    type="button"
                    aria-pressed={active}
                    data-active={active}
                    className="chams-focusable min-h-10 rounded-md px-2 font-heading text-xs font-semibold text-muted-foreground hover:bg-sunken data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                    onClick={() => {
                      onChange(`${year}-${String(month + 1).padStart(2, "0")}`);
                      setOpen(false);
                    }}
                  >
                    {monthName}
                  </button>
                );
              })}
            </div>
          )}
          {view === "years" && (
            <div className="grid grid-cols-3 gap-1.5">
              {years.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  aria-pressed={candidate === year}
                  data-active={candidate === year}
                  className="chams-figure chams-focusable min-h-11 rounded-md px-2 font-heading text-xs font-semibold text-muted-foreground hover:bg-sunken data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                  onClick={() => {
                    setYear(candidate);
                    setView("months");
                  }}
                >
                  {new Intl.NumberFormat(language, { useGrouping: false }).format(candidate)}
                </button>
              ))}
            </div>
          )}
        </div>
      </CalendarPopover>
    </div>
  );
}
